import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';

export interface SeoMetaData {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  robots?: string;
  url?: string;
}

@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly appName = 'ComplyDesk';
  private readonly defaultTitle = 'ComplyDesk | Tax Compliance Software';
  private readonly defaultDescription =
    'ComplyDesk simplifies tax compliance, filings, and payment workflows for Nigerian businesses and professional service providers.';
  private readonly defaultKeywords =
    'tax compliance, tax filing, TIN application, VAT filing, Nigerian tax software, corporate tax, tax preparation';
  private readonly defaultRobots = 'index, follow';
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);

  updateTitle(title?: string): void {
    const pageTitle = title ? `${title} | ${this.appName}` : this.defaultTitle;
    this.title.setTitle(pageTitle);
    this.meta.updateTag({ property: 'og:title', content: pageTitle });
    this.meta.updateTag({ name: 'twitter:title', content: pageTitle });
  }

  updateMeta(data: SeoMetaData = {}): void {
    const description = data.description ?? this.defaultDescription;
    const keywords = data.keywords ?? this.defaultKeywords;
    const robots = data.robots ?? this.defaultRobots;
    const url = this.resolveUrl(data.url);
    const image = data.image ?? this.resolveImageUrl('/assets/images/logo/comply-desk-logo.png');

    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    this.meta.updateTag({ name: 'keywords', content: keywords });
    this.meta.updateTag({ name: 'robots', content: robots });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:url', content: url });
    this.meta.updateTag({ property: 'og:image', content: image });
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:image', content: image });

    this.updateCanonical(url);
  }

  private resolveUrl(url?: string): string {
    if (url?.startsWith('http')) {
      return url;
    }

    const canonicalPath = url?.startsWith('/') ? url : `/${url ?? ''}`;
    const baseUrl = this.getOrigin();

    return `${baseUrl}${canonicalPath}`.replace(/([^:])\/\/+/g, '$1/');
  }

  private resolveImageUrl(path: string): string {
    const origin = this.getOrigin();
    return origin ? `${origin}${path}` : path;
  }

  private getOrigin(): string {
    if (isPlatformBrowser(this.platformId) && typeof window !== 'undefined') {
      return window.location.origin;
    }

    // During SSR/prerendering there is no real browser origin (Angular Universal
    // resolves document.location to something like http://localhost:4200), which
    // would bake the wrong canonical/og:url into the static prerendered HTML.
    return 'https://complydeskng.com';
  }

  private updateCanonical(url: string): void {
    if (!url) {
      return;
    }

    let link: HTMLLinkElement | null = this.document.head.querySelector('link[rel="canonical"]');

    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }

    link.setAttribute('href', url);
  }

  updateStructuredData(schema: object | object[] | null | undefined): void {
    const existing = this.document.head.querySelector('script[data-seo-structured-data]');
    existing?.remove();

    if (!schema) {
      return;
    }

    const payload = Array.isArray(schema)
      ? { '@context': 'https://schema.org', '@graph': schema }
      : { '@context': 'https://schema.org', ...schema };

    const script = this.document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo-structured-data', 'true');
    script.text = JSON.stringify(payload);
    this.document.head.appendChild(script);
  }
}
